import React, { useState } from 'react';
import Select, { createFilter } from 'react-select';
import './AsyncTypeahead.scss';
import { FormGroup, ControlLabel } from 'react-bootstrap';
import { getClosestElement } from '../../utils/getClosestElement';
import { OptionTypeBase } from 'react-select';
import { LabelMeta } from 'components/Typeahead/Typeahead';
import { useDebounceCallback } from '../../utils/useDebounceCallback';
import uuid from '../../utils/uuid';
import { useDirtyWarningContext, DirtyWarningInputProps } from '../DirtyWarning';

export interface AsyncTypeaheadProps extends DirtyWarningInputProps {
    label?: string;
    key?: string;
    id?: string;
    inputId?: string;
    name: string;
    /** options that are id:name by default.  use getOptionValue to override id and getOptionLabel to override name */
    defaultOptions?: any[];
    value?: any;
    inputValue?: string;
    placeholder?: string;
    multiple?: boolean;
    ref?: any;
    backspaceRemovesValue?: boolean;
    escapeClearsValue?: boolean;
    /** provide a function that takes in a searchTerm and returns the array of options from the API */
    onSearch: (searchTerm: string) => Promise<any[]>
    onLoading?: (loading?: boolean) => void;
    searchDelay?: number;
    onChange: (selected: any, actionMeta?: string | any ) => void;
    onBlur?: (e: any) => void;
    onFocus?: (e: any) => void;
    onInputChange?: (newValue: string, actionMeta?: string | any) => void;
    getOptionLabel?: (option: any) => any;
    getOptionValue?: (option: any) => any;
    renderMenuItemChildren?: (option: any, metadata?: LabelMeta) => React.ReactNode;
    refCallback?: (ref: any) => any;
    isOptionSelected?: (option:any) => boolean;
};

let lastSearchId = uuid();

export const AsyncTypeahead: React.FC<AsyncTypeaheadProps> = (props) => {
    const context = useDirtyWarningContext();

    const { 
        onBlur,
        onChange, 
        ref, 
        label, 
        renderMenuItemChildren, 
        onSearch, 
        getOptionLabel, 
        getOptionValue, 
        name, 
        defaultOptions,
        searchDelay,
        backspaceRemovesValue,
        escapeClearsValue,
        disableDirtyTracking = false,
        ...otherProps
    } = props;

    const getOptionLabelFn = getOptionLabel || function (option: any) { return option.name };
    const getOptionValueFn = getOptionValue || function (option: any) { return option.id };

    const [options, setOptions] = useState(defaultOptions || []);
    const [isLoading, setIsLoading] = useState(false);
    
    async function internalSearch(val: string) {
        try {
            let currentId = uuid();
            lastSearchId = currentId;
            const newOptions = await props.onSearch(val);
            if (lastSearchId === currentId) {
                setOptions(newOptions);
                internalUpdateLoading(false);
            }
        }
        catch (err) {
            setOptions([]);
        }
    }

    const internalUpdateLoading = (val: boolean) => {
        setIsLoading(val);
        if (props.onLoading) props.onLoading(val);
    }

    const debouncedSearch = useDebounceCallback(internalSearch, searchDelay || 750)
    
    function handleInputChange(val: string) {
        if (val && val.length > 0) {
            internalUpdateLoading(true);
            setOptions([]);
            debouncedSearch(val);
        }
    }

    //TODO: Replace react-bootstrap-typeahead.  This prevents the blur if we click on the dropdown menu's scrollbar.
    let wrapperOnBlur: any = undefined;
    if (onBlur) {
        wrapperOnBlur = function wrapperOnBlur(e: any) {

            //all other browsers use event.relatedTarget
            //ie11 requires using document.activeElement for blurs
            const relatedTarget = e.relatedTarget || document.activeElement;

            if (getClosestElement(relatedTarget, '.blur-wrapper') != null) {
                e.preventDefault();
            } else {
                onBlur(e);
            }
        }
    }

    let formatOptionLabelImpl: any = undefined;
    if (renderMenuItemChildren) {
        formatOptionLabelImpl = function formatOptionLabelImpl(option: OptionTypeBase, labelMeta: LabelMeta) {
            return renderMenuItemChildren(option, labelMeta);
        }
    }

    const defaultIsOptionSelected = (o: any, options:any) => o === props.value || (options.length && options.includes(o));

    const handleChange = (selected: any, actionMeta?: string | any ) => {
        if (onChange) {
            onChange(selected, actionMeta)
        }

        if (!disableDirtyTracking) {
            context.setDirty(true);
        }
    }

    const asyncTypeAhead = <div tabIndex={-1} onBlur={wrapperOnBlur} className="blur-wrapper">
        <Select
            name={name}
            isClearable
            escapeClearsValue={escapeClearsValue || true}
            backspaceRemovesValue={backspaceRemovesValue || true}
            noOptionsMessage={({ inputValue }) => inputValue ? "No results found." : null }
            placeholder={props.placeholder || "< Select >"}
            isLoading={isLoading}
            onInputChange={handleInputChange}
            multiple={props.multiple || false}
            formatOptionLabel={formatOptionLabelImpl}
            getOptionLabel={getOptionLabelFn}
            getOptionValueFn={getOptionValueFn}
            ref={props.refCallback}
            isOptionSelected={props.isOptionSelected || defaultIsOptionSelected}
            options={options}
            filterOption={createFilter({ ignoreAccents: false })}
            onChange={handleChange}
            {...otherProps}
        />
    </div>

    if (!props.label) return asyncTypeAhead;

    return (
        <FormGroup controlId={props.key}>
            <ControlLabel>{props.label}</ControlLabel>
            {asyncTypeAhead}
        </FormGroup>

    )
}

(AsyncTypeahead as any).displayName = 'AsyncTypeahead';